import { v4 as uuid } from 'uuid';
import UmlConnector from '../../Types/UmlConnector';
import { flattenClassShapes } from './CanvasShapeFlattener';
import dependencyResolver from './DependencyResolver';
import outputMsgEngine from '../../../Widgets/OutputMsgWidget/Core/OutputMsgEngine';

/**
 * Builds one 'association' connector per constructor dependency (resolved
 * via DependencyResolver) that resolves, by name, to another entry in the
 * SAME list. Works on a flat, language-agnostic shape: {id, name, members} —
 * doesn't matter whether entries came from a fresh C# parse or from real
 * UmlClassShape/UmlMemberText instances already on the canvas (see
 * buildCanvasAssociationConnectors below). Unresolved dependencies (BCL
 * types, or a type not in `entries`) are silently skipped. De-duped against
 * BOTH existingConnectors and connectors already queued within this call.
 * @param {{id: string, name: string, members: object[]}[]} entries
 * @param {import('../../Types/UmlConnector').default[]} existingConnectors
 * @returns {import('../../Types/UmlConnector').default[]}
 */
export function buildAssociationConnectors(entries, existingConnectors) {
    const nameToId = new Map();
    entries.forEach((entry) => {
        if (entry.name && !nameToId.has(entry.name)) {
            nameToId.set(entry.name, entry.id);
        }
    });

    const seenPairs = new Set(
        existingConnectors.map((c) => `${c.fromShapeId}->${c.toShapeId}`),
    );

    const connectors = [];
    const dependencies = dependencyResolver.resolveDependencies(entries);

    dependencies.forEach(({ fromId, toName }) => {
        const toId = nameToId.get(toName);
        if (toId === fromId) return; // self-dependency, skip silently
        if (!toId) {
            if (dependencyResolver.isLikelyUserType(toName)) {
                outputMsgEngine.addMsg('UmlWorkspace', 'warning', `Dependency not found: ${toName}`);
            }
            return;
        }

        const pairKey = `${fromId}->${toId}`;
        if (seenPairs.has(pairKey)) return;

        seenPairs.add(pairKey);
        connectors.push(new UmlConnector(uuid(), fromId, toId, 'association'));
    });

    return connectors;
}

/**
 * Retroactive, whole-canvas pass — part of the "Find Connectors" button in
 * UmlSchemaImportTab.jsx (see CanvasConnectorFinder.js), runs against
 * whatever is CURRENTLY on the canvas, no new parsing involved.
 * @param {import('../../Types/UmlShape').default[]} shapes - umlCanvasEngine.shapes (top-level)
 * @param {import('../../Types/UmlConnector').default[]} existingConnectors - umlCanvasEngine.connectors
 * @returns {import('../../Types/UmlConnector').default[]}
 */
export function buildCanvasAssociationConnectors(shapes, existingConnectors) {
    const entries = flattenClassShapes(shapes).map((shape) => ({
        id: shape.id,
        name: shape.caption?.text,
        members: shape.members,
    }));

    return buildAssociationConnectors(entries, existingConnectors);
}
